import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Checkbox,
  TextField,
  useTheme,
  Stack,
  Card,
  CardContent,
  Divider,
  IconButton,
  Avatar,
  alpha,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { isNull, isUndefined } from "lodash";
import AcceptQuatationSubmit from "@/app/features/tender/components/view/quatation-accept-sbmit";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { Close as CloseIcon } from "@mui/icons-material";
import RefuseQuatationSubmit from "@/app/features/tender/components/view/quatation-refuse-sbmit";
import { ROLE } from "@/types";
import { showToast } from "@/app/global-components/toaster";
import useRegisterCompany from "@/hooks/use-register-company";
import useOrder from "@/hooks/use-order";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import useOffer from "@/hooks/useTender";
import OrderQuatationSubmit from "../../order-quatation-submit";
import { routes } from "@/consts";
import { Link } from "react-router-dom";
import {
  Business,
  AttachMoney,
  LocalShipping,
  Schedule,
  Description,
} from "@mui/icons-material";

interface QuotationDetailsModalProps {
  open: boolean;
  isInfo?: boolean;
  onClose: () => void;
  quotation: any | null;
  offerStatus?: string;
}

interface Product {
  productId: string;
  unitName: string;
  quantity: number;
  unitPrice: number;
  vat: number;
  notice: string;
  attachment?: File | null;
  image?: { url: string } | null | undefined;
}

interface CompanySchema {
  id: string;
  nameAr: string;
  nameEn: string;
  CompanyNr: string;
  website?: string;
  city?: string;
  region?: string;
  orderNr: number;
}

const QuotationDetailsModal: React.FC<QuotationDetailsModalProps> = ({
  open,
  onClose,
  isInfo,
  quotation,
  offerStatus,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();

  if (!quotation) return null;

  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getCompany } = useRegisterCompany();
  const [company, setCompany] = useState<CompanySchema | null>(null);
  const { createOrder, isOrderLoading } = useOrder();
  const { acceptQuatation, refuseQuatation, isLoading, error } = useOffer();

  const totalPrice = quotation.products.reduce(
    (acc: any, product: any) =>
      acc +
      (product.unitPrice ?? 0) *
        (product.quantity ?? 0) *
        (1 + (product.vat ?? 0) / 100),
    0
  );

  const totalVat = quotation.products.reduce(
    (acc: any, product: any) =>
      acc +
      (product.unitPrice ?? 0) *
        (product.quantity ?? 0) *
        ((product.vat ?? 0) / 100),
    0
  );

  const getDateLabel = (days: string) => {
    switch (days) {
      case "7":
        return `7 ${t("time.days")}`;
      case "14":
        return `14 ${t("time.days")}`;
      case "30":
        return `30 ${t("time.days")}`;
      default:
        return `7 ${t("time.days")}`;
    }
  };

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [Edquotation, setEdquotation] = useState<any>(quotation);

  const handleAction = async (action: Function, id: string | undefined) => {
    if (quotation && id) {
      try {
        await action(id);
        onClose();
      } catch (err) {
        console.error("Error performing action:", err);
      }
    }
  };

  const handleCheckboxChange = (productId: string) => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(productId)
        ? prevSelected.filter((id) => id !== productId)
        : [...prevSelected, productId]
    );
  };

  const handleCreateOrder = async () => {
    if (Object.keys(selectedItems).length === 0) {
      showToast(t("Alert.SelectAtLeastOneItem"), "warning");
      return;
    }

    const selectedProducts = Edquotation.products
      .filter((product: Product) => selectedItems.includes(product.productId))
      .map((product: any) => ({
        itemId: product.productId,
        item: product.unitName,
        quantity: product.quantity,
        price: product.unitPrice,
        vat: product.vat || "15",
        discount: 0,
        notice: "",
        image: product.image,
        attachment: product.attachment,
      }));

    const orderData = {
      title: "Order for Tender",
      endDate: new Date().toISOString(),
      deliverDate: new Date(
        new Date().getTime() + 7 * 24 * 60 * 60 * 1000
      ).toISOString(),
      type: "Tiny",
      status: "Planning",
      region: myCompany.region,
      city: myCompany.city,
      address: myCompany.address,
      fileId: Edquotation.fileId,
      tenderId: Edquotation.tenderId,
      companyId: Edquotation.companyId,
      userId: authUser?.id,
      products: selectedProducts,
      Sendedproducts: [],
      OrderNr: company ? company.orderNr + 1 : 1,
      DeliveryMethod: Edquotation.DeliveryMethod,
      paymentMethod: Edquotation.paymentMethod,
    };

    try {
      await createOrder(orderData);
      await acceptQuatation(Edquotation.id);
      showToast(t("Alert.OrderCreated"), "success");
      onClose();
    } catch (err) {
      console.error("Error creating order:", err);
    }
  };

  const handleQuantityChange = (productId: string, value: number) => {
    const updatedProducts = Edquotation.products.map((product: Product) =>
      product.productId === productId
        ? { ...product, quantity: value }
        : product
    );
    setEdquotation({
      ...quotation,
      products: updatedProducts,
    });
  };

  useEffect(() => {
    getCompany(Edquotation.companyId).then((Company) => {
      setCompany(Company.data);
    });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Description className="text-purple-600" sx={{ fontSize: 28 }} />
            <Typography variant="h5" fontWeight={600}>
              {t("quotations.details")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.grey[500],
              "&:hover": {
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Fade in={true}>
          <Stack spacing={3}>
            {/* Company and Value Information */}
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Business color="primary" sx={{ fontSize: 24 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("company.fields.name")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600}>
                          {quotation.company?.CompanyNr}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <AttachMoney color="primary" sx={{ fontSize: 24 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("tender.fields.value")}
                        </Typography>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          color="primary"
                        >
                          {quotation.value} {t("currency")}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Card
              elevation={2}
              sx={{
                borderRadius: 3,
                boxShadow: "none",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                  {t("products.list")}
                </Typography>

                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                        }}
                      >
                        {(offerStatus === "Closed" ||
                          offerStatus === "Accepted") &&
                          userRole === "Buyer" && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={
                                  selectedItems.length ===
                                  Edquotation.products.length
                                }
                                indeterminate={
                                  selectedItems.length > 0 &&
                                  selectedItems.length <
                                    Edquotation.products.length
                                }
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems(
                                      Edquotation.products.map(
                                        (p: any) => p.productId
                                      )
                                    );
                                  } else {
                                    setSelectedItems([]);
                                  }
                                }}
                              />
                            </TableCell>
                          )}
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("item.fields.image")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("checkout.fields.name")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("checkout.fields.quantity")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("item.fields.unit")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {t("orders.fields.notes")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Edquotation.products.map((product: any) => (
                        <TableRow key={product.productId} hover>
                          {(offerStatus === "Closed" ||
                            offerStatus === "Accepted") &&
                            userRole === "Buyer" && (
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedItems.includes(
                                    product.productId
                                  )}
                                  onChange={() =>
                                    handleCheckboxChange(product.productId)
                                  }
                                />
                              </TableCell>
                            )}
                          <TableCell>
                            {product.image ? (
                              <Avatar
                                src={product.image}
                                alt="product"
                                sx={{ width: 50, height: 50 }}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  width: 50,
                                  height: 50,
                                  bgcolor: theme.palette.grey[300],
                                }}
                              >
                                —
                              </Avatar>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {isInfo ? product.unitName : product.item}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {selectedItems.includes(product.productId) ? (
                              <TextField
                                type="number"
                                size="small"
                                value={product.quantity}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    product.productId,
                                    parseInt(e.target.value)
                                  )
                                }
                                inputProps={{ min: 1 }}
                                sx={{ width: 80 }}
                              />
                            ) : (
                              <Typography variant="body2">
                                {product.quantity}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {isInfo ? product.unitPrice : product.unit}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {product.notice || "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Additional Information */}
            {isInfo && (
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  boxShadow: "none",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    {t("general.information")}
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Schedule color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t("delivery.suggestedTime")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {isNull(quotation.deliverDays)
                              ? "—"
                              : `${quotation.deliverDays} ${t("time.days")}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Schedule color="action" />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t("offers.validityPeriod1")}
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {isNull(quotation.DeadLineDate)
                              ? "—"
                              : getDateLabel(quotation.DeadLineDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <LocalShipping color="action" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t("delivery.title")}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {isNull(quotation.DeliveryMethod)
                          ? "—"
                          : quotation.DeliveryMethod}
                      </Typography>
                    </Box>
                  </Box>

                  {userRole !== ROLE.BUYER && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <AttachMoney color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {t("payment.method1")}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {isNull(quotation.paymentMethod)
                            ? "—"
                            : quotation.paymentMethod === "1"
                            ? t("payment.direct")
                            : t("payment.deferred1")}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Price Summary */}
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t("item.fields.vat")}:{" "}
                      <strong>
                        {totalVat.toFixed(2)} {t("currency")}
                      </strong>
                    </Typography>
                    <Typography variant="h6" color="primary" fontWeight={600}>
                      {t("orders.fields.totalPrice")}: {totalPrice.toFixed(2)}{" "}
                      {t("currency")}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {error && (
              <Card
                elevation={2}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.error.main}`,
                  bgcolor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Fade>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {(offerStatus === "Closed" || offerStatus === "Accepted") &&
            userRole === ROLE.BUYER && (
              <OrderQuatationSubmit
                isLoading={isOrderLoading}
                onClick={handleCreateOrder}
              />
            )}

          {offerStatus === "Closed" && userRole === ROLE.BUYER && (
            <AcceptQuatationSubmit
              isLoading={isLoading}
              onClick={() => handleAction(acceptQuatation, quotation.id)}
            />
          )}

          {userRole === ROLE.BUYER && (
            <RefuseQuatationSubmit
              isLoading={isLoading}
              onClick={() => handleAction(refuseQuatation, quotation.id)}
            />
          )}

          <Button
            variant="outlined"
            onClick={onClose}
            className="!border-zinc-900 !text-zinc-900"
            sx={{ borderRadius: 2 }}
          >
            {t("actions.close")}
          </Button>

          {!isInfo && (
            <Button
              component={Link}
              to={`${routes.vuetender}/${quotation.id}`}
              className="!bg-zinc-900 !text-white"
              sx={{ borderRadius: 2 }}
            >
              {t("actions.fillNow")}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default QuotationDetailsModal;
